import Divider from '@shared/components/Divider/Divider';
import InputGroup from '@shared/components/InputGroup/InputGroup';
import Popup from '@shared/components/Popup/Popup';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ModalFooter from 'components/ModalFooter';
import { WorkspaceRoleV2 } from 'modules/CreateWorkspaceModal/types';
import WorkspaceFormField from 'modules/CreateWorkspaceModal/WorkspaceCreateStep/WorkspaceFormField';
import PermissionCategories from 'modules/CreateWorkspaceModal/WorkspaceRoleCreate/PermissionCategories';
import { WorkspaceRolesFormType } from 'modules/CreateWorkspaceModal/WorkspaceRoleCreate/useWorkspaceRolesForm';
import useUpdateWorkspaceRole from 'modules/Workspaces/hooks/useUpdateWorkspaceRole/useUpdateWorkspaceRole';

import DeleteRoleButton from './DeleteRoleButton';
import useWorkspaceRoleEditForm from '../WorkspaceRolesSetting/useWorkspaceRoleEditForm';

interface WorkspaceRoleEditFormProps {
  role: WorkspaceRoleV2;
  workspaceId: string;
  onClose: () => void;
}

const WorkspaceRoleEditForm = ({ role, workspaceId, onClose }: WorkspaceRoleEditFormProps) => {
  const { t } = useTranslation();
  const { updateWorkspaceRole, isLoading } = useUpdateWorkspaceRole();
  const form = useWorkspaceRoleEditForm(role.id, role.name, role);

  const {
    formState: { errors, isValid },
    handleSubmit: onSubmitForm,
    register,
  } = form;

  const handleSubmit = ({ roleName, permissions }: WorkspaceRolesFormType) =>
    updateWorkspaceRole({
      workspaceId,
      roleId: role.id,
      name: roleName.trim(),
      permissions,
    }).then(onClose);

  return (
    <FormProvider {...form}>
      <Popup isVisible closePopup={onClose} wrapperId="workspace-role-edit-modal">
        <Popup.FullScreenContainer className="md:w-[658px] md:max-w-[658px]">
          <div className="px-4">
            <Popup.Header className="shadow-none border-none !mx-0">
              <span className="text-size-md font-medium">{t('workspace_roles.edit.title')}</span>
              <Popup.CloseIcon closePopup={onClose} className="!right-0" />
            </Popup.Header>
            <Divider className="hidden md:block" />
          </div>
          <form onSubmit={onSubmitForm(handleSubmit)} className="flex flex-col grow overflow-y-auto gap-4 px-4 md:pt-4">
            <InputGroup label={t('workspaces.name_role')} errorMessage={errors.roleName?.message}>
              <InputGroup.Input
                {...register('roleName')}
                placeholder={t('workspaces.role_placeholder')}
                className="border-primary-50"
                hasError={!!errors.roleName?.message}
                autoComplete="off"
              />
            </InputGroup>
            <WorkspaceFormField label={t('workspaces.role_permissions')}>
              <PermissionCategories className="mb-4" isEditOrCreate />
            </WorkspaceFormField>
          </form>
          <ModalFooter
            onCloseClick={onClose}
            onSubmit={onSubmitForm(handleSubmit)}
            successButtonText={t('save')}
            cancelText={t('cancel')}
            successDisabled={!isValid || isLoading}
            isLoading={isLoading}
            deleteLayout={!role.defaultRoleType}
            leftSideButton={
              role.defaultRoleType ? undefined : (
                <DeleteRoleButton roleId={role.id} roleName={role.name} onDeleted={onClose} />
              )
            }
          />
        </Popup.FullScreenContainer>
      </Popup>
    </FormProvider>
  );
};

export default WorkspaceRoleEditForm;
